import { deepClone } from './helpers';
import { DEFAULT_ANSWER, DEFAULT_SEPARATOR, DEFAULT_LINEBREAK, DEFAULT_SNOMED_SYSTEM } from './constants';
import { getPage, getSection, getQuestion, getAnswer, getAnswers, getQuestions, getSections } from './helpers';

/**
 * Creates all handler functions for the form builder
 * @param {Function} updateSchema - Function to update the schema
 * @param {Function} updateQuestionField - Function to update a question field
 * @param {Function} showNotification - Function to show notifications
 */
export const createHandlers = (updateSchema, updateQuestionField, showNotification) => {
  const handleAddPage = () => {
    updateSchema((newSchema) => {
      if (!newSchema.pages) newSchema.pages = [];
      newSchema.pages.push({
        label: `Page ${newSchema.pages.length + 1}`,
        sections: []
      });
    });
  };

  const handleAddSection = (pageIndex) => {
    updateSchema((newSchema) => {
      const page = getPage(newSchema, pageIndex);
      if (!page) return;
      if (!page.sections) page.sections = [];
      page.sections.push({
        label: `Section ${page.sections.length + 1}`,
        isExpanded: 'true',
        columnLayout: 1,
        showTitleInPreview: true,
        questions: []
      });
    });
  };

  const handleAddQuestion = (pageIndex, sectionIndex) => {
    updateSchema((newSchema) => {
      const section = getSection(newSchema, pageIndex, sectionIndex);
      if (!section) return;
      if (!section.questions) section.questions = [];
      section.questions.push({
        label: 'New Question',
        id: `question_${Date.now()}`,
        type: 'obs',
        questionOptions: { rendering: 'text', concept: '' }
      });
    });
    showNotification('Success', 'Question added');
  };

  const handleAddRichText = (pageIndex, sectionIndex) => {
    updateSchema((newSchema) => {
      const section = getSection(newSchema, pageIndex, sectionIndex);
      if (!section) return;
      if (!section.questions) section.questions = [];
      section.questions.push({
        label: 'Rich Text Content',
        id: `richtext_${Date.now()}`,
        type: 'richtext',
        content: '<p>Enter your formatted text, instructions, or information here...</p>'
      });
    });
    showNotification('Success', 'Rich Text added');
  };

  const handleUpdateQuestion = (pageIndex, sectionIndex, questionIndex, field, value) => {
    updateQuestionField(pageIndex, sectionIndex, questionIndex, (question) => {
      if (field === 'label' || field === 'id' || field === 'type') {
        question[field] = value;
      } else if (field === 'rendering' || field === 'concept') {
        if (!question.questionOptions) question.questionOptions = {};
        question.questionOptions[field] = value;
        // If changing to checkbox-with-select, initialize structure
        if (field === 'rendering' && value === 'checkbox-with-select') {
          if (!question.questionOptions.answers) question.questionOptions.answers = [];
          question.questionOptions.layout = question.questionOptions.layout || 'vertical';
          question.questionOptions.selectPlaceholder = question.questionOptions.selectPlaceholder || 'Please specify';
        }
      }
    });
  };

  const handleDeleteQuestion = (pageIndex, sectionIndex, questionIndex) => {
    updateSchema((newSchema) => {
      const questions = getQuestions(newSchema, pageIndex, sectionIndex);
      if (!questions) return;
      questions.splice(questionIndex, 1);
    });
    showNotification('Success', 'Question deleted');
  };

  const handleCloneQuestion = (pageIndex, sectionIndex, questionIndex) => {
    updateSchema((newSchema) => {
      const questions = getQuestions(newSchema, pageIndex, sectionIndex);
      if (!questions?.[questionIndex]) return;
      const clonedQuestion = deepClone(questions[questionIndex]);
      if (clonedQuestion.id) clonedQuestion.id = `${clonedQuestion.id}_${Date.now()}`;
      questions.splice(questionIndex + 1, 0, clonedQuestion);
    });
    showNotification('Success', 'Question cloned');
  };

  const handleMoveSection = (pageIndex, sectionIndex, direction) => {
    updateSchema((newSchema) => {
      const sections = getSections(newSchema, pageIndex);
      if (!sections) return;
      const newIndex = direction === 'up' ? sectionIndex - 1 : sectionIndex + 1;
      if (newIndex < 0 || newIndex >= sections.length) return;
      [sections[sectionIndex], sections[newIndex]] = [sections[newIndex], sections[sectionIndex]];
    });
  };

  const handleMoveQuestion = (pageIndex, sectionIndex, questionIndex, direction) => {
    updateSchema((newSchema) => {
      const questions = getQuestions(newSchema, pageIndex, sectionIndex);
      if (!questions) return;
      const newIndex = direction === 'up' ? questionIndex - 1 : questionIndex + 1;
      if (newIndex < 0 || newIndex >= questions.length) return;
      [questions[questionIndex], questions[newIndex]] = [questions[newIndex], questions[questionIndex]];
    });
  };

  const handleDeleteSection = (pageIndex, sectionIndex) => {
    updateSchema((newSchema) => {
      const sections = getSections(newSchema, pageIndex);
      if (!sections) return;
      sections.splice(sectionIndex, 1);
    });
    showNotification('Success', 'Section deleted');
  };

  const handleCloneSection = (pageIndex, sectionIndex) => {
    updateSchema((newSchema) => {
      const sections = getSections(newSchema, pageIndex);
      if (!sections?.[sectionIndex]) return;
      const clonedSection = deepClone(sections[sectionIndex]);
      if (clonedSection.label) clonedSection.label = `${clonedSection.label} (Copy)`;
      sections.splice(sectionIndex + 1, 0, clonedSection);
    });
    showNotification('Success', 'Section cloned');
  };

  const handleDeletePage = (pageIndex) => {
    updateSchema((newSchema) => {
      newSchema.pages.splice(pageIndex, 1);
    });
    showNotification('Success', 'Page deleted');
  };

  const handleAddAnswer = (pageIndex, sectionIndex, questionIndex) => {
    updateQuestionField(pageIndex, sectionIndex, questionIndex, (question) => {
      if (!question.questionOptions) question.questionOptions = {};
      if (!question.questionOptions.answers) question.questionOptions.answers = [];
      question.questionOptions.answers.push({ ...DEFAULT_ANSWER });
    });
  };

  const handleAddSeparator = (pageIndex, sectionIndex, questionIndex) => {
    updateQuestionField(pageIndex, sectionIndex, questionIndex, (question) => {
      if (!question.questionOptions) question.questionOptions = {};
      if (!question.questionOptions.answers) question.questionOptions.answers = [];
      question.questionOptions.answers.push({ ...DEFAULT_SEPARATOR });
    });
  };

  const handleAddLineBreak = (pageIndex, sectionIndex, questionIndex) => {
    updateQuestionField(pageIndex, sectionIndex, questionIndex, (question) => {
      if (!question.questionOptions) question.questionOptions = {};
      if (!question.questionOptions.answers) question.questionOptions.answers = [];
      question.questionOptions.answers.push({ ...DEFAULT_LINEBREAK });
    });
  };

  const handleMoveAnswer = (pageIndex, sectionIndex, questionIndex, answerIndex, direction) => {
    updateSchema((newSchema) => {
      const answers = getAnswers(newSchema, pageIndex, sectionIndex, questionIndex);
      if (!answers) return;
      const newIndex = direction === 'up' ? answerIndex - 1 : answerIndex + 1;
      if (newIndex < 0 || newIndex >= answers.length) return;
      [answers[answerIndex], answers[newIndex]] = [answers[newIndex], answers[answerIndex]];
    });
  };

  const handleUpdateAnswer = (pageIndex, sectionIndex, questionIndex, answerIndex, field, value) => {
    updateSchema((newSchema) => {
      const answer = getAnswer(newSchema, pageIndex, sectionIndex, questionIndex, answerIndex);
      if (!answer) return;
      answer[field] = value;
    });
  };

  const handleDeleteAnswer = (pageIndex, sectionIndex, questionIndex, answerIndex) => {
    updateSchema((newSchema) => {
      const answers = getAnswers(newSchema, pageIndex, sectionIndex, questionIndex);
      if (!answers) return;
      answers.splice(answerIndex, 1);
    });
    showNotification('Success', 'Answer deleted');
  };

  const handleCloneAnswer = (pageIndex, sectionIndex, questionIndex, answerIndex) => {
    updateSchema((newSchema) => {
      const answers = getAnswers(newSchema, pageIndex, sectionIndex, questionIndex);
      if (!answers?.[answerIndex]) return;
      answers.splice(answerIndex + 1, 0, deepClone(answers[answerIndex]));
    });
    showNotification('Success', 'Answer cloned');
  };

  const handleUpdateQuestionLayout = (pageIndex, sectionIndex, questionIndex, layout) => {
    updateQuestionField(pageIndex, sectionIndex, questionIndex, (question) => {
      if (!question.questionOptions) question.questionOptions = {};
      question.questionOptions.layout = layout;
    });
  };

  const handleTerminologyChange = (pageIndex, sectionIndex, questionIndex, answerIndex, terminologyObject) => {
    updateSchema((newSchema) => {
      const answer = getAnswer(newSchema, pageIndex, sectionIndex, questionIndex, answerIndex);
      if (!answer) return;
      
      if (terminologyObject) {
        answer.terminology = terminologyObject;
        answer.conceptCode = terminologyObject.code;
        answer.concept = terminologyObject.code;
        if (terminologyObject.display) {
          answer.label = terminologyObject.display;
        }
      } else {
        answer.terminology = null;
        answer.conceptCode = '';
        answer.concept = '';
      }
    });
  };

  const handleSystemChange = (pageIndex, sectionIndex, questionIndex, answerIndex, value) => {
    updateSchema((newSchema) => {
      const answer = getAnswer(newSchema, pageIndex, sectionIndex, questionIndex, answerIndex);
      if (!answer) return;
      answer.conceptSystem = value;
      answer.conceptCode = '';
      answer.concept = '';
      answer.terminology = null;
    });
  };

  return {
    handleAddPage,
    handleAddSection,
    handleAddQuestion,
    handleAddRichText,
    handleUpdateQuestion,
    handleDeleteQuestion,
    handleCloneQuestion,
    handleMoveSection,
    handleMoveQuestion,
    handleDeleteSection,
    handleCloneSection,
    handleDeletePage,
    handleAddAnswer,
    handleAddSeparator,
    handleAddLineBreak,
    handleMoveAnswer,
    handleUpdateAnswer,
    handleDeleteAnswer,
    handleCloneAnswer,
    handleUpdateQuestionLayout,
    handleTerminologyChange,
    handleSystemChange
  };
};

